import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import clsx from "clsx";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { CData } from "src/Data";
import * as Dati from "src/Dati";
import * as Glob from "src/Globale";
import { Trad } from "src/components/";
import { _Autocomplete, _DateField, _NumberField, _Select } from "../../form";
import {
    _CompaniesGroupDetails,
    _CoInsurance,
    _CheckFileList,
    _PolicyNeutralFieldsList
} from "..";
import { IRouteParam } from "../../pages/PolicyNeutral";

interface IPolicyFormContentProps {
    BondData: any;
    Data: Dati.CRisAPIDatoTipiInfo_Polizza;
    Limits: Dati.Dictionary<number>;
    ProductBinder: string;
    RiduzioneSommaGarantita: number | undefined;
    OnBlurAmount: () => void;
    OnChangeAmount: (e: string, val: string) => void;
    OnChangeBondData: (e: string, val: unknown) => void;
    OnChangeDerogation: (e: React.FormEvent<any>, tipo: string, verso: string) => void;
    OnChange: (e: React.FormEvent<any> | string, val?: unknown) => void;
}

const useStyles = makeStyles(({ spacing }: Theme) => ({
    state: {
        marginTop: spacing(1)
    }
}));

export const _PolicyNeutralFormContent: React.FC<IPolicyFormContentProps> = ({
    BondData,
    Data,
    Limits,
    ProductBinder,
    RiduzioneSommaGarantita,
    OnBlurAmount,
    OnChangeAmount,
    OnChangeBondData,
    OnChangeDerogation,
    OnChange,
}) => {
    const classes = useStyles();
    const { ID_Anag, ID_Polizza } = useParams<IRouteParam>();
    const OnChangeProductType = (e: React.FormEvent<any> | string, val?: unknown) => {
        OnChange(e, val);
        OnChange("Info", {});
    };
    
    const OnChangeBinder = (e: React.FormEvent<any> | string, val?: string) => {
        OnChange(e, val);
        OnChange("Dato.CoAssicurazioneJSON", GetCoInsuranceJson(val ?? ""));
    };
    
    const SetDisabledDates = (): boolean[] => {
        let isStartDateDisabled = false;
        let isEndDateDisabled = false;

        for (var key in Data.Info) {
            if (Data.Info[key].Campo == "DataPresentazione") {
                isStartDateDisabled = true;
            }
            if (Data.Info[key].Campo == "DataSvincolo") {
                isEndDateDisabled = true;
            }
        }

        return [isStartDateDisabled, isEndDateDisabled];
    };

    const OnChangeDataEffetto = (name: string, val: string | Date) => {
        if (BondData.DataSvincolo != undefined && BondData.Durata != undefined) {
            const DataEffetto: moment.Moment = moment(val);
            let DataSvincolo: moment.Moment = moment(BondData.DataSvincolo);
            const Durata: number = DataSvincolo.diff(DataEffetto, "days");

            if (DataEffetto.toDate() > DataSvincolo.toDate()) {
                DataSvincolo = DataEffetto;
                OnChange("Dato.DataFine", DataSvincolo.toDate());
                OnChangeBondData("DataSvincolo", DataSvincolo.toDate());
            }
            OnChangeBondData("Durata", Durata);
        }
        OnChange(name, val);
    };

    const OnChangePresentationDate = (name: string, val: string | Date) => {
        if (BondData.DataPresentazione != undefined && BondData.DataSvincolo != undefined && BondData.Durata != undefined) {
            const presentationDate: moment.Moment = moment(val);
            const releaseDate: Date = new CData(presentationDate.toDate(), true).AddGiorni(Number(BondData.Durata)).Data;

            OnChange("Dato.DataEffetto", presentationDate.toDate());
            OnChange("Dato.DataFine", releaseDate);
            OnChangeBondData("DataSvincolo", releaseDate);
        }
        OnChangeBondData(name, val);
    };

    const OnChangeReleaseDate = (name: string, val: string | Date) => {
        const releaseDate: moment.Moment = moment(val);

        if (BondData.DataPresentazione != undefined && BondData.DataSvincolo != undefined && BondData.Durata != undefined) {
            let presentationDate: moment.Moment = moment(BondData.DataPresentazione);

            if (presentationDate.toDate() > releaseDate.toDate()) {
                presentationDate = releaseDate;
                OnChangeBondData("DataPresentazione", presentationDate.toDate());
            }

            OnChangeBondData("Durata", releaseDate.diff(presentationDate, "days"));
            OnChange("Dato.DataFine", releaseDate.toDate());
            OnChange("Dato.DataEffetto", presentationDate.toDate());
        } else if (Data.Dato.DataEffetto != undefined && BondData.DataSvincolo != undefined && BondData.Durata != undefined) {
            let dataEffetto: moment.Moment = moment(Data.Dato.DataEffetto);

            if (dataEffetto.toDate() > releaseDate.toDate()) {
                dataEffetto = releaseDate;
                OnChange("Dato.DataEffetto", dataEffetto.toDate());
            }

            OnChangeBondData("Durata", releaseDate.diff(dataEffetto, "days"));
            OnChange("Dato.DataFine", releaseDate.toDate());
        }
        OnChangeBondData(name, val);
    };

    const OnChangeDuration = (e: string, val: string | number) => {
        if (BondData.DataPresentazione != undefined && BondData.DataSvincolo != undefined) {
            const presentationDate: Date = moment(BondData.DataPresentazione).toDate();
            const releaseDate: Date = new CData(presentationDate, true).AddGiorni(Number(val)).Data;

            OnChangeBondData("DataSvincolo", releaseDate);
            OnChange("Dato.DataFine", releaseDate);
            OnChange("Dato.DataEffetto", presentationDate);
        } else if (Data.Dato.DataEffetto != undefined && BondData.DataSvincolo != undefined) {
            const releaseDate: Date = new CData(moment(Data.Dato.DataEffetto).toDate(), true).AddGiorni(Number(val)).Data;

            OnChangeBondData("DataSvincolo", releaseDate);
            OnChange("Dato.DataFine", releaseDate);
        }
        OnChangeBondData(e, val);
    };

    const GetCoInsuranceJson = (productBinder: string): string => {
        if (productBinder && productBinder.length === 23) {
            const coInsurance = new Dati.CDatoCoAssicurazione();
            coInsurance.Delegataria = true;
            coInsurance.Quota = 100;
            coInsurance.ID_Binder = parseInt(productBinder.substr(0, 7));
            coInsurance.ID_BinderCompagnia = parseInt(productBinder.substr(8, 7));
            coInsurance.ID_Anag = parseInt(productBinder.substr(16, 7));

            return JSON.stringify([coInsurance]);
        }

        return "";
    };
    
    const [isDataEffettoDisabled, isDataFineDisabled] = useMemo(SetDisabledDates, [Data.Info]);
    
    return (
        <Grid item container spacing={4}>
            <Grid item xs={12} md={2}>
                <TextField fullWidth disabled id="Dato.ID_Polizza" label={Trad.Trad("ID")} value={Data.Dato.ID_Polizza} onChange={OnChange} />
            </Grid>
            <Grid item xs={12} md={4}>
                <_Autocomplete
                    Disabled
                    Value={Data.Dato.ID_AnagPOS || ""}
                    Name="Dato.ID_AnagPOS"
                    Label="POS"
                    OnChange={OnChange}
                    Url="/api/Anag/RicercaVeloce?id_TipoAnag=&ricerca="
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <_Autocomplete
                    Disabled
                    Value={Data.Dato.ID_AnagContraente || ""}
                    Name="Dato.ID_AnagContraente"
                    Label="Contraente"
                    OnChange={OnChange}
                    Url="/api/Anag/RicercaVeloce?id_TipoAnag=&ricerca="
                />
            </Grid>
            <Grid item xs={12} md={2}>
                {Data.Dato.StatoPolizza && (
                    <>
                        <InputLabel shrink>{Trad.Trad("Stato polizza")}</InputLabel>
                        <Chip
                            label={Trad.Trad(Data.Dato.StatoPolizza)}
                            size="small"
                            className={clsx(classes.state, {
                                warning: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_Preventivo,
                                info: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_PreventivoBozzaInviata,
                                success: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_Emessa,
                                error: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_Rifiuto
                            })}
                        />
                    </>
                )}
            </Grid>
            <Grid item xs={12} md={2}>
                <TextField
                    fullWidth
                    disabled
                    id="Dato.Polizza"
                    label={Trad.Trad("Polizza")}
                    value={Data.Dato.Polizza || ""}
                    onChange={OnChange}
                    helperText={Glob.AzioneAbilitata("CodiceControlloVisualizza", Glob.Lettura) && Data.Dato.CodiceControllo}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <_Autocomplete
                    SearchInput
                    Value={Data.Dato.ID_TipoProdotto}
                    Name="Dato.ID_TipoProdotto"
                    Label="Prodotto"
                    Disabled={!!Number(ID_Polizza)}
                    OnChange={OnChangeProductType}
                    Url={`/api/Tipi/Tipo?tabella=TipiProdotti&campo=TipoProdotto`}
                />
            </Grid>
            <Grid item xs={12} md={2}>
                <_DateField Value={Data.Dato.DataEffetto} Name="Dato.DataEffetto" Label="Data effetto" OnChange={OnChangeDataEffetto} Disabled={isDataEffettoDisabled} />
            </Grid>
            <Grid item xs={12} md={2}>
                <_DateField Value={Data.Dato.DataFine} Name="Dato.DataFine" Label="Data fine" OnChange={OnChange} Disabled={isDataFineDisabled} />
            </Grid>
            <Grid item xs={12} md={6}>
                <_Autocomplete
                    Value={ProductBinder}
                    Name="ListaBinderJSON"
                    Label="ListaBinder"
                    OnChange={OnChangeBinder}
                    Url={`/api/Binder/ListaBinderDisponibili?id_AnagDa=${Data.Dato.ID_AnagPOS}`}
                    SearchInput
                />
            </Grid>
            {BondData.ATI && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2">{Data.Dato.ContraenteATI}</Typography>
                    <_CompaniesGroupDetails Data={BondData.MandantiJSON} ParentCompanyColumn={false} OnChange={val => OnChangeBondData("MandantiJSON", val)} />
                </Grid>
            )}
            <Grid item xs={12}>
                <_PolicyNeutralFieldsList
                    BondData={BondData}
                    Info={Data.Info}
                    Limits={Limits}
                    RiduzioneSommaGarantita={RiduzioneSommaGarantita}
                    OnBlurAmount={OnBlurAmount}
                    OnChangeAmount={OnChangeAmount}
                    OnChangeBondData={OnChangeBondData}
                    OnChangeDerogation={OnChangeDerogation}
                    OnChangeDuration={OnChangeDuration}
                    OnChangePresentationDate={OnChangePresentationDate}
                    OnChangeReleaseDate={OnChangeReleaseDate}
                />
            </Grid>
            {Data.Dato.ID_Polizza > 0 && (
                <Grid item xs={12}>
                    <_CheckFileList ValueJSON={Data.Dato.FileJSON} ID_TipoFileRichiesto={Data.Dato.ID_TipoFileRichiesto || ""} OnChange={OnChange} />
                </Grid>
            )}
        </Grid>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/PolicyNeutral/PolicyNeutralFormContent.tsx